import React, { useMemo, useState } from "react";
import { FormInstance } from "antd/es/form/Form";
import {
  Form,
  Row,
  Col,
  Button,
  Input,
  Tooltip,
  Table,
  Select,
  Collapse,
  Pagination,
  Popconfirm,
} from "antd";
import tickIcon from "../../img/done.svg";
import deleteIcon from "../../img/delete-round.svg";
import edit from "../../img/edit.svg";
import { checkIsLoaded, checkIsLoading, checkNotLoaded } from "../../lib/utils/checkLoadingStatus";
import {
  deletePropertyType,
  fetchPropertyPageSearchTypes,
  updatePropertyType,
} from "../../store/slices/property-type-admin";
import TablePageSelect from "../TablePageSelect/TablePageSelect";
import { ReactComponent as DeleteIcon } from "../../img/delete.svg";
import PropertyTypesAdminCreate from "../PropertyTypesAdminCreate/PropertyTypesAdminCreate";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ADMIN_PROPERTY_TYPE_WRITE } from "../../lib/const/permissions";
import { useAppDispatch, useAppSelector } from "../..";
import { PageableSearchResult, PropertyTypeSearchParams, PropertyType } from "../../lib/interfaces";
import { LoadingStatusCode } from "../../lib/types";
import "./PropertyTypesAdmin.less";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const buttonLayout = {
  wrapperCol: { span: 24 },
};

type EditingStatus = "" | "editingForce" | "deleteForce";

interface ComponentProps {
  form: FormInstance<any>;
  formFilters: FormInstance<any>;
  getValuePropertyType: (field: string) => number | string;
  handlePageSize: (field: "size" | "page", value: number) => void;
  handleSearch: (fields: PropertyTypeSearchParams) => void;
  propertyPageSearchTypes?: PageableSearchResult<PropertyType[]>;
  propertyPageSearchTypesLoading: LoadingStatusCode;
  propertyTypeSearchParams: PropertyTypeSearchParams;
  defaultPropertyTypeSearchParams: PropertyTypeSearchParams;
  resetSearchPayments: () => void;
  editStatus: "" | "editingForce" | "deleteForce";
  setEditStatus: React.Dispatch<React.SetStateAction<EditingStatus>>;
}

const PropertyTypesAdmin = ({
  form,
  formFilters,
  getValuePropertyType,
  handlePageSize,
  handleSearch,
  propertyPageSearchTypes,
  propertyPageSearchTypesLoading,
  propertyTypeSearchParams,
  defaultPropertyTypeSearchParams,
  resetSearchPayments,
  editStatus,
  setEditStatus,
}: ComponentProps) => {
  const dispatch = useAppDispatch();

  const { TextArea } = Input;
  const { Panel } = Collapse;
  const { Option } = Select;
  const {
    dicDataTypes,
    dicDataTypesLoading,
    dicRequiredType,
    dicRequiredTypeLoading,
    dicMultiplType,
    dicMultiplTypeLoading,
  } = useAppSelector((state) => state.rtkDictionaries);
  const { dictionaryTypes } = useAppSelector((state) => state.dicts);
  const [editingKey, setEditingKey] = useState<string>();
  const isEditing = (rec: PropertyType) => rec.key === editingKey;
  const isEditingForce = editStatus === "editingForce";
  const isDeleteForce = editStatus === "deleteForce";
  const [dataUpdate, setDataUpdate] = useState<
    Pick<PropertyType, "group" | "name" | "dsc" | "requiredTypeId" | "multiplTypeId">
  >({
    name: "",
  });
  const [filtersCollapseState, setFiltersCollapseState] = useState("unfolded");
  const [formCreate, setFormCreate] = useState(false);

  const getTableHeight = () => (filtersCollapseState === "unfolded" ? `${47}vh` : `${63}vh`);

  const getPanelHeader = () =>
    filtersCollapseState === "unfolded" ? (
      ""
    ) : (
      <div className="bulk__panel_header">Показать фильтры</div>
    );

  const toggleFiltersCollapseState = () => {
    const newState = filtersCollapseState === "unfolded" ? "folded" : "unfolded";
    setFiltersCollapseState(newState);
  };

  const tenantName = [
    {
      id: 2,
      name: "Kirgizstan",
    },
    {
      id: 1,
      name: "Kazahstan",
    },
    {
      id: 7,
      name: "Russia",
    },
    {
      id: 0,
      name: "default",
    },
  ];

  const pageSizeOptions = {
    20: 20,
    50: 50,
    100: 100,
  };

  const renderFilters = () => (
    <div className="filter-collapse property-type-filters">
      <Row>
        <Col span={7}>
          <Form.Item label="Объект" name="objectTypeAlias">
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col span={7} offset={1}>
          <Form.Item label="Наименование" name="name">
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col span={7} offset={1}>
          <Form.Item label="Alias" name="propertyTypeAlias">
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item {...buttonLayout} style={{ textAlign: "right", marginBottom: 0 }}>
        <Button onClick={resetSearchPayments}>Сброс параметров</Button>
        <Button
          loading={checkIsLoading(propertyPageSearchTypesLoading)}
          type="primary"
          htmlType="submit"
        >
          Применить
        </Button>
      </Form.Item>
    </div>
  );

  const handleTerminCreateForm = () => {
    setFormCreate(true);
  };

  const tableData: PropertyType[] = useMemo(() => {
    if (checkNotLoaded(propertyPageSearchTypesLoading) || !propertyPageSearchTypes) {
      return [];
    }
    return propertyPageSearchTypes.content.map((item) => ({
      key: String(item.id),
      ...item,
    }));
  }, [propertyPageSearchTypes, propertyPageSearchTypesLoading]);

  const columns = [
    {
      title: "Объект",
      dataIndex: "objectTypeSysname",
      key: "objectTypeSysname",
    },
    {
      title: "Alias",
      dataIndex: "alias",
      key: "alias",
    },
    {
      title: "Группа свойств",
      dataIndex: "group",
      key: "group",
      render: (val: string, record: PropertyType) => {
        const editable = isEditing(record);
        const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
          setDataUpdate({ ...dataUpdate, group: e.target.value });
        };
        return editable && isEditingForce ? (
          <>
            <Input defaultValue={val} onChange={onChangeValue} />
          </>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      render: (val: string, record: PropertyType) => {
        const editable = isEditing(record);
        const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
          setDataUpdate({ ...dataUpdate, name: e.target.value });
        };
        return editable && isEditingForce ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="name"
            rules={[{ required: true, message: "" }]}
          >
            <Input onChange={onChangeValue} />
          </Form.Item>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "Комментарий",
      dataIndex: "dsc",
      key: "dsc",
      render: (val: string, record: PropertyType) => {
        const editable = isEditing(record);
        const onChangeValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          setDataUpdate({ ...dataUpdate, dsc: e.target.value });
        };
        return editable && isEditingForce ? (
          <>
            <TextArea defaultValue={val} onChange={onChangeValue} />
          </>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "Тенант",
      dataIndex: "tenantId",
      key: "tenantId",
      render: (val: number) => {
        const dataName = tenantName && tenantName.find((item) => item.id === val)?.name;
        return <>{dataName}</>;
      },
    },
    {
      title: "Тип данных",
      dataIndex: "dataType",
      key: "dataType",
      render: (val: number) => {
        const dataName =
          checkIsLoaded(dicDataTypesLoading) && dicDataTypes.find((item) => item.id === val)?.name;
        return <>{dataName}</>;
      },
    },
    {
      title: "Обязательность",
      dataIndex: "requiredTypeId",
      key: "requiredTypeId",
      render: (val: number, record: PropertyType) => {
        const editable = isEditing(record);
        const onChangeValue = (value: number) => {
          setDataUpdate({ ...dataUpdate, requiredTypeId: value });
        };
        const dataName =
          checkIsLoaded(dicRequiredTypeLoading) &&
          dicRequiredType.find((item) => item.id === val)?.name;

        return editable && isEditingForce ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="requiredTypeId"
            rules={[{ required: true, message: "" }]}
          >
            <Select style={{ width: "100%" }} onChange={onChangeValue}>
              {checkIsLoaded(dicRequiredTypeLoading) &&
                dicRequiredType.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        ) : (
          <>{dataName}</>
        );
      },
    },
    {
      title: "Множественность значений",
      dataIndex: "multiplTypeId",
      key: "multiplTypeId",
      render: (val: number, record: PropertyType) => {
        const editable = isEditing(record);
        const onChangeValue = (value: number) => {
          setDataUpdate({ ...dataUpdate, multiplTypeId: value });
        };
        const dataName =
          checkIsLoaded(dicMultiplTypeLoading) &&
          dicMultiplType.find((item) => item.id === val)?.name;
        return editable && isEditingForce ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="multiplTypeId"
            rules={[{ required: true, message: "" }]}
          >
            <Select style={{ width: "100%" }} onChange={onChangeValue}>
              {checkIsLoaded(dicMultiplTypeLoading) &&
                dicMultiplType.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        ) : (
          <>{dataName}</>
        );
      },
    },
    {
      title: "Словарь (параметр)",
      dataIndex: "specificDicType",
      key: "specificDicType",
      render: (val: number, record) => {
        const dataName = dictionaryTypes?.find((item) => item.alias === record.specificDicTypeAlias)
          ?.name;
        return <>{dataName}</>;
      },
    },
    {
      title: "Словарь (значение)",
      dataIndex: "valDicType",
      key: "valDicType",
      render: (val: number, record) => {
        const dataName = dictionaryTypes?.find((item) => item.alias === record.valDicTypeAlias)
          ?.name;
        return <>{dataName}</>;
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "x",
      width: "7%",
      render: (record: PropertyType) => {
        const editable = isEditing(record);
        const disabled = isEditingForce;

        const handleEdit = (editStatus: EditingStatus) => {
          form.resetFields();
          setEditingKey(record.key);
          setEditStatus(editStatus);
          setDataUpdate({
            ...dataUpdate,
            name: record.name,
            dsc: record.dsc,
            group: record.group,
            requiredTypeId: record.requiredTypeId,
            multiplTypeId: record.multiplTypeId,
          });
        };

        const handleCancel = () => {
          form.resetFields();
          setEditStatus("");
          setEditingKey(undefined);
        };

        const handleSubmit = () => {
          form.validateFields().then(async () => {
            if (isEditingForce) {
              const dataPost: PropertyType = {
                ...record,
                ...dataUpdate,
                propertyForDic: false,
              };
              if (record.objectTypeSysname && record.alias) {
                await dispatch(
                  updatePropertyType({
                    objectTypeSysname: record.objectTypeSysname,
                    propertyTypeAlias: record.alias,
                    dataPost,
                  })
                );
              }
            }
            if (isDeleteForce) {
              if (record.objectTypeSysname && record.alias) {
                await dispatch(
                  deletePropertyType({
                    objectTypeSysname: record.objectTypeSysname,
                    propertyTypeAlias: record.alias,
                  })
                );
              }
            }

            await dispatch(fetchPropertyPageSearchTypes(propertyTypeSearchParams));
            setEditingKey(undefined);
            setEditStatus("");
            form.resetFields();
          });
        };

        return editable && isEditingForce ? (
          <>
            <Tooltip title="Подтвердить">
              <Button
                icon={<img src={tickIcon} alt="tick" width="16px" />}
                className="icon"
                type="text"
                htmlType="button"
                onClick={handleSubmit}
              />
            </Tooltip>
            <Button
              icon={<img src={deleteIcon} width="16px" alt="delete" />}
              type="text"
              className="icon"
              htmlType="button"
              onClick={handleCancel}
            />
          </>
        ) : (
          <>
            {hasAccess(MENU_ADMIN_PROPERTY_TYPE_WRITE) && record.tenantId !== 0 && (
              <div style={{ display: "flex" }}>
                <Tooltip title="Редактировать">
                  <Button
                    icon={<img src={edit} alt="pencil" width="16px" />}
                    type="text"
                    htmlType="button"
                    disabled={disabled}
                    onClick={() => handleEdit("editingForce")}
                  />
                </Tooltip>
                {disabled ? (
                  <Button
                    icon={<DeleteIcon />}
                    onClick={() => handleEdit("deleteForce")}
                    style={{ outline: "none" }}
                    disabled={disabled}
                    className="button-icon"
                  />
                ) : (
                  <Popconfirm
                    title="Вы действительно хотите удалить свойство?"
                    placement="left"
                    onCancel={(e: React.MouseEvent<HTMLElement> | undefined) =>
                      e?.stopPropagation()
                    }
                    onConfirm={(e: React.MouseEvent<HTMLElement> | undefined) => {
                      if (e) {
                        e.stopPropagation();
                      }
                      handleSubmit();
                    }}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <Button
                      icon={<DeleteIcon />}
                      onClick={() => handleEdit("deleteForce")}
                      style={{ outline: "none" }}
                      disabled={disabled}
                      className="button-icon"
                    />
                  </Popconfirm>
                )}
              </div>
            )}
          </>
        );
      },
    },
  ];

  return (
    <>
      {!formCreate ? (
        <>
          <Form
            form={formFilters}
            labelAlign="left"
            initialValues={defaultPropertyTypeSearchParams}
            {...layout}
            onFinish={(values: PropertyTypeSearchParams) => {
              if (values) {
                values.page = 0;
                values.sort = ["object_type_id,asc", "id,asc", "tenantId,desc"];
                values.size = propertyTypeSearchParams.size;
              }
              handleSearch(values);
            }}
          >
            <div className="inner-row" style={{ marginBottom: "15px" }}>
              <Row align="middle" justify="space-between">
                <h1>Типы свойств</h1>
                <Col>
                  {hasAccess(MENU_ADMIN_PROPERTY_TYPE_WRITE) && (
                    <Button onClick={handleTerminCreateForm} type="primary">
                      Добавить тип свойства
                    </Button>
                  )}
                </Col>
              </Row>
              <Collapse
                defaultActiveKey="1"
                onChange={toggleFiltersCollapseState}
                expandIconPosition="right"
                className="filter-collapse"
              >
                <Panel header={getPanelHeader()} key="1">
                  {renderFilters()}
                </Panel>
              </Collapse>
            </div>
          </Form>
          <Row justify="end" align="middle" className=" account-detalization__pagination">
            <Col span={12}>
              <Row justify="center">
                <Pagination
                  onChange={(e) => handlePageSize("page", e)}
                  current={Number(getValuePropertyType("page")) + 1}
                  total={propertyPageSearchTypes?.totalElements}
                  pageSize={Number(getValuePropertyType("size"))}
                  hideOnSinglePage
                  size="small"
                  showSizeChanger={false}
                />
              </Row>
            </Col>
            <Col span={6} style={{ paddingRight: "15px" }}>
              <Row justify="end" align="middle">
                <Col style={{ marginRight: "30px" }}>
                  <TablePageSelect
                    onChange={(e: number) => handlePageSize("size", e)}
                    value={getValuePropertyType("size")}
                    pageSizeOptions={pageSizeOptions}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Form form={form}>
            <Table
              size="small"
              className="tenant-table version-inner-table"
              columns={columns}
              scroll={{ x: 1600, y: getTableHeight() }}
              dataSource={tableData || []}
              pagination={false}
              loading={checkIsLoading(propertyPageSearchTypesLoading)}
            />
          </Form>
        </>
      ) : (
        <PropertyTypesAdminCreate
          setFormCreate={setFormCreate}
          propertyTypeSearchParams={propertyTypeSearchParams}
        />
      )}
    </>
  );
};

export default PropertyTypesAdmin;
